import { FormEvent, useState } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  CalendarIcon,
  Eye,
  EyeOff,
  IdCard,
  Lock,
  Mail,
  Phone,
  User,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { cn } from '@/shared/utils/cn';
import { normalizeEmail } from '@/shared/utils/normalize-email';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { useToast } from '@/shared/ui/toast-provider';
import { postAuthSignUp, type SignUpRequest } from '@/entities/auth';
import { resolveErrorMessage } from '@/shared/lib/errors';

const backgroundImage =
  'https://images.unsplash.com/photo-1497366858526-0766cadbe8fa?q=80&w=2069&auto=format&fit=crop';

export const RegisterPage = () => {
  const router = useRouter();
  const toast = useToast();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseExpiryDate, setLicenseExpiryDate] = useState<
    Date | undefined
  >();
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const hasRequiredFields = Boolean(
    fullName.trim() &&
      email.trim() &&
      password.trim() &&
      confirmPassword.trim(),
  );

  const signUpMutation = useMutation({
    mutationFn: postAuthSignUp,
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Account created',
        description: 'Check your inbox and verify your email to continue.',
      });
      router.navigate({
        to: '/verify-email',
        search: { email: normalizeEmail(email) },
      });
    },
    onError: (error) => {
      toast({
        type: 'error',
        title: 'We could not create your account.',
        description: resolveErrorMessage(error, 'Please try again.'),
      });
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!hasRequiredFields) {
      setError('Please enter the required details.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setError(null);

    const payload: SignUpRequest = {
      email: normalizeEmail(email),
      password,
      password_confirm: confirmPassword,
      name: fullName.trim(),
      phone_number: phoneNumber ? phoneNumber : null,
      license_number: licenseNumber ? licenseNumber : null,
      license_expiry_date: licenseExpiryDate
        ? format(licenseExpiryDate, 'yyyy-MM-dd')
        : null,
      date_of_birth: dateOfBirth ? format(dateOfBirth, 'yyyy-MM-dd') : null,
      address: null,
    };

    try {
      await signUpMutation.mutateAsync(payload);
    } catch {
      // handled in onError
    }
  };

  const formattedLicenseExpiryDate = licenseExpiryDate
    ? format(licenseExpiryDate, 'MM-dd-yyyy')
    : null;
  const formattedDateOfBirth = dateOfBirth
    ? format(dateOfBirth, 'MM-dd-yyyy')
    : null;

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-3 pb-12 pt-28 text-slate-50">
      <img
        src={backgroundImage}
        alt="Registration background"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-slate-950/65" aria-hidden />

      <Link
        to="/sign-in"
        search={{ redirect: undefined }}
        className="group absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/60 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-slate-950/40 backdrop-blur transition hover:bg-slate-900/80 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 transition group-hover:-translate-x-0.5" />
        Back
      </Link>

      <section className="relative z-10 w-full max-w-[420px] space-y-6 rounded-[28px] border border-white/10 bg-slate-900/80 p-8 text-slate-50 shadow-[0_30px_80px_rgba(10,17,33,0.55)] backdrop-blur-2xl">
        <header className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-white">
            Create Account
          </h1>
          <p className="text-sm text-slate-200/80">
            Join our community of real estate professionals
          </p>
        </header>

        <form className="space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-2.5">
            <div className="relative">
              <label htmlFor="fullName" className="sr-only">
                Full Name
              </label>
              <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="fullName"
                type="text"
                autoComplete="name"
                aria-required
                placeholder="Full Name *"
                value={fullName}
                onChange={(event) => {
                  if (error) setError(null);
                  setFullName(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
            </div>

            <div className="relative">
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                aria-required
                placeholder="Email *"
                value={email}
                onChange={(event) => {
                  if (error) setError(null);
                  setEmail(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
            </div>

            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                aria-required
                placeholder="Password *"
                value={password}
                onChange={(event) => {
                  if (error) setError(null);
                  setPassword(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pr-11 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-200/80 transition hover:bg-white/10 hover:text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="relative">
              <label htmlFor="confirmPassword" className="sr-only">
                Confirm Password
              </label>
              <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                aria-required
                placeholder="Confirm Password *"
                value={confirmPassword}
                onChange={(event) => {
                  if (error) setError(null);
                  setConfirmPassword(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pr-11 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-slate-200/80 transition hover:bg-white/10 hover:text-white"
                aria-label={
                  showConfirmPassword
                    ? 'Hide confirm password'
                    : 'Show confirm password'
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="relative">
              <label htmlFor="phoneNumber" className="sr-only">
                Phone Number
              </label>
              <Phone className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="phoneNumber"
                type="tel"
                autoComplete="tel"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(event) => {
                  if (error) setError(null);
                  setPhoneNumber(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
            </div>

            <div className="relative">
              <label htmlFor="licenseNumber" className="sr-only">
                WA State License Number
              </label>
              <IdCard className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                id="licenseNumber"
                type="text"
                placeholder="WA State License #"
                value={licenseNumber}
                onChange={(event) => {
                  if (error) setError(null);
                  setLicenseNumber(event.target.value);
                }}
                className="h-11 rounded-2xl border-white/20 bg-white/10 pl-10 text-sm text-slate-100 placeholder:text-slate-200/60 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0"
              />
            </div>

            <div className="relative">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex h-11 w-full items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-3.5 pr-11 text-left text-sm text-slate-100 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0',
                      !formattedLicenseExpiryDate && 'text-slate-200/60',
                    )}
                  >
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                    {formattedLicenseExpiryDate ??
                      'License Expiry Date (MM-DD-YYYY)'}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto border border-white/20 bg-slate-900/95 p-3 text-slate-50"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={licenseExpiryDate}
                    onSelect={setLicenseExpiryDate}
                  />
                </PopoverContent>
              </Popover>
              {licenseExpiryDate && (
                <button
                  type="button"
                  onClick={() => setLicenseExpiryDate(undefined)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-200"
                  aria-label="Clear license expiry date"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="relative">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'flex h-11 w-full items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-3.5 pr-11 text-left text-sm text-slate-100 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 focus-visible:ring-offset-0',
                      !formattedDateOfBirth && 'text-slate-200/60',
                    )}
                  >
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                    {formattedDateOfBirth ?? 'Date of Birth (MM-DD-YYYY)'}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto border border-white/20 bg-slate-900/95 p-3 text-slate-50"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={dateOfBirth}
                    onSelect={setDateOfBirth}
                    captionLayout="dropdown"
                    dropdownClassNames={{
                      trigger:
                        'border-white/20 bg-white/10 text-slate-100 focus:ring-blue-500/60 [&>svg]:text-slate-300',
                      content:
                        'border-white/20 bg-slate-900/95 text-slate-50',
                      item: 'text-slate-100 focus:bg-white/10 focus:text-white',
                    }}
                    defaultMonth={dateOfBirth ?? new Date(2000, 0)}
                    startMonth={new Date(1950, 0)}
                    endMonth={new Date()}
                    disabled={{ after: new Date() }}
                  />
                </PopoverContent>
              </Popover>
              {dateOfBirth && (
                <button
                  type="button"
                  onClick={() => setDateOfBirth(undefined)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-200"
                  aria-label="Clear date of birth"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {error ? (
            <p className="text-sm font-semibold text-amber-300">{error}</p>
          ) : null}

          <p className="rounded-2xl border border-white/20 bg-white/5 p-2.5 text-xs leading-relaxed text-slate-200/80">
            By creating an account, you agree to our Terms of Service and
            Privacy Policy.
          </p>

          <Button
            type="submit"
            variant="glow"
            size="lg"
            className="w-full gap-2 text-sm h-11"
            disabled={!hasRequiredFields || signUpMutation.isPending}
          >
            {signUpMutation.isPending
              ? 'Creating account...'
              : 'Create Account'}
            {!signUpMutation.isPending ? (
              <ArrowRight className="h-4 w-4" />
            ) : null}
          </Button>
        </form>

        <footer className="flex items-center justify-center gap-1 text-sm text-slate-200/80 pt-2">
          <span>Already have an account?</span>
          <Link
            to="/sign-in"
            search={{ redirect: undefined }}
            className="font-semibold text-sky-300 transition hover:text-sky-200"
          >
            Sign in
          </Link>
        </footer>
      </section>
    </main>
  );
};
